'use client';

import { IconSearch } from '@/components/ui/icons';
import { useAuth } from '@/context/auth';
import { useUserData } from '@/context/auth/user-data-context';
import { Link } from '@/i18n/navigation';
import LogoImage from '@public/logo-bunqer.svg';
import { Menu, X } from 'lucide-react';
import { useTranslations } from 'next-intl';
import Image from 'next/image';
import { useEffect, useRef, useState } from 'react';
import AccountDropDown from './account-dropdown';
import CartButton from './cart-button';
import { LocaleSwitcher } from './locale-switcher';
import { Navigation } from './navigation';
import NavigationLink from './navigation-link';
import SearchBar from './search-bar';

export default function Header() {
    const [isMenuOpen, setMenuOpen] = useState(false);
    const menuRef = useRef<HTMLDivElement>(null);
    const searchRef = useRef<HTMLDivElement>(null);
    const [isScrolled, setIsScrolled] = useState(false);
    const { isBuyer, isAnonymous, loading } = useUserData();
    const [isSearchOpen, setSearchOpen] = useState<boolean>(false);
    const { signout } = useAuth();

    const t = useTranslations('Navigation');
    const th = useTranslations('Header');

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
                setMenuOpen(false);
            }
            if (searchRef.current && !searchRef.current.contains(event.target as Node)) {
                setSearchOpen(false);
            }
        };

        if (isMenuOpen) {
            document.addEventListener('click', handleClickOutside);
        }
        if (isSearchOpen) {
            document.addEventListener('click', handleClickOutside);
        }

        return () => {
            document.removeEventListener('click', handleClickOutside);
        };
    }, [isMenuOpen, isSearchOpen]);

    useEffect(() => {
        const handleScroll = () => {
            setIsScrolled(window.scrollY > 10);
        };

        handleScroll();

        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    return (
        <header className={'sticky top-0 left-0 w-full z-50 text-white'}>
            {!loading ? (
                <>
                    <div
                        className={`sm:px-20  py-5 px-8 w-full transition-all duration-300 relative z-2 ${
                            isMenuOpen ? 'bg-[rgba(0,0,0,1)]' : ''
                        } ${isScrolled ? 'transition-colors bg-[rgba(0,0,0,0.9)]' : ''}`}
                    >
                        <div className="max-w-8xl mx-auto flex items-center">
                            <Link href="/" className="flex-shrink-0 me-auto sm:me-[initial]">
                                <Image src={LogoImage.src} alt="bunqer" width={110} height={46} />
                            </Link>

                            <div className="hidden lg:flex justify-start text-white ml-15 transition-all duration-300">
                                <Navigation />
                            </div>

                            <div className="hidden sm:block m-0 sm:m-auto ms-[initial] sm:ms-auto">
                                <SearchBar
                                    className="bg-[rgba(255,255,255,0.10)] rounded-[32px]"
                                    title={t('searchPlaceholder')}
                                />
                            </div>

                            <div className="hidden lg:flex items-center gap-4 transition-all duration-300">
                                <LocaleSwitcher className="transition duration-300 rounded-[8px]" />
                                {isBuyer && <CartButton isScrolled={isScrolled} className="px-[16px] py-[12px]" />}

                                <AccountDropDown isScrolled={isScrolled} />
                            </div>

                            <div className="block sm:hidden leading-[1]" ref={searchRef}>
                                <button
                                    className="border-0 bg-[transparent] p-0 m-0"
                                    onClick={() => {
                                        setSearchOpen(!isSearchOpen);
                                        setMenuOpen(false);
                                    }}
                                    type="button"
                                >
                                    <IconSearch />
                                </button>
                                <div
                                    className={`
                                        absolute left-0 top-[85px] w-[100%]
                                        ${isSearchOpen ? 'block' : 'hidden'}
                                    `}
                                >
                                    <SearchBar
                                        title={t('searchPlaceholder')}
                                        className="flex-row-reverse bg-[rgba(0,0,0,0.9)]"
                                        onSearchDone={() => setSearchOpen(false)}
                                    />
                                </div>
                            </div>
                            {isAnonymous ? (
                                <AccountDropDown margin="ml-[28px]" iconOnly={true} className="lg:hidden text-white" />
                            ) : null}

                            {isBuyer && (
                                <CartButton
                                    isScrolled={isScrolled}
                                    className="lg:hidden px-[0px] py-[0px] ml-5"
                                    isMobile={true}
                                />
                            )}

                            <button
                                type="button"
                                className="lg:hidden ml-5 text-white transition-all duration-300"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setMenuOpen(!isMenuOpen);
                                    setSearchOpen(false);
                                }}
                            >
                                {isMenuOpen ? <X size={28} /> : <Menu size={28} />}
                            </button>
                        </div>
                    </div>

                    <div
                        ref={menuRef}
                        className={`lg:hidden h-[calc(100vh-65px)] md:h-auto w-full md:w-[33vw] fixed right-0 top-[85px] md:top-[90px] bg-black text-white px-4 pt-12 lg:pt-4 pb-6 transition-all duration-300 ease-in-out shadow-xl z-1 ${
                            isMenuOpen ? 'translate-y-0 opacity-100' : '-translate-y-10 opacity-0 pointer-events-none'
                        }`}
                    >
                        <Navigation isMobile onLinkClickAction={() => setMenuOpen(false)} />
                        <div className="mt-3 flex flex-row gap-4 justify-between">
                            <LocaleSwitcher />
                        </div>
                        {!isAnonymous ? (
                            <div className="mt-3">
                                <button
                                    type="button"
                                    onClick={signout}
                                    className="inline-block px-3 py-3 transition-colors cursor-pointer"
                                >
                                    {th('Sign out')}
                                </button>
                            </div>
                        ) : null}
                    </div>
                </>
            ) : null}
        </header>
    );
}
